import './MatchesView.css'
import './MatchDetailView.css'

import { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import { session, matches, safety, agentChat, profile } from '../store'

import { useRequireAuthentication } from '../hooks/useRequireAuthentication'
import { useRequireOnboarding } from '../hooks/useRequireOnboarding'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { PersonProfile } from '../components/PersonProfile'
import { Button } from '../components/Button'
import { Form } from '../components/Form'
import { SelectField } from '../components/SelectField'
import { TextField } from '../components/TextField'

const reportReasonItems = [
  { value: 'harassment', label: 'Harassment' },
  { value: 'spam', label: 'Spam or solicitation' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'fake', label: 'Fake profile' },
  { value: 'underage', label: 'Underage concern' },
  { value: 'safety', label: 'Safety concern' },
  { value: 'other', label: 'Other' },
]

type SafetyFormValue = {
  reason: string
  details: string
}

export function MatchDetailView() {
  useRequireAuthentication()
  useRequireOnboarding()

  const { profileId } = useParams<{ profileId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [, setBreadcrumbs] = useBreadcrumbs()

  const authStatus = useAtomValue(session.sessionStatusAtom)
  const matchList = useAtomValue(matches.matchesAtom)
  const isLoading = useAtomValue(matches.matchesIsLoadingAtom)
  const error = useAtomValue(matches.matchesErrorAtom)
  const safetyError = useAtomValue(safety.safetyErrorAtom)
  const safetyLoading = useAtomValue(safety.safetyIsLoadingAtom)
  const attributes = useAtomValue(profile.profileAttributesAtom)

  const fetchMatches = useSetAtom(matches.fetchMatchesAtom)
  const expressInterest = useSetAtom(matches.expressInterestAtom)
  const unmatch = useSetAtom(matches.unmatchAtom)
  const openChat = useSetAtom(agentChat.agentChatOpenAtom)
  const blockUser = useSetAtom(safety.blockUserAtom)
  const reportUser = useSetAtom(safety.reportUserAtom)
  const fetchAttributes = useSetAtom(profile.fetchProfileAttributesAtom)
  const clearProfile = useSetAtom(profile.clearProfileAtom)

  const [actionPending, setActionPending] = useState(false)
  const [showSafety, setShowSafety] = useState(false)
  const [safetyForm, setSafetyForm] = useState<SafetyFormValue>({
    reason: 'harassment',
    details: '',
  })
  const [safetyMessage, setSafetyMessage] = useState<string | null>(null)
  const [introductionEnded, setIntroductionEnded] = useState(false)

  const match = matchList.find((m) => m.profile.id === profileId)

  useEffect(() => {
    if (authStatus === 'authenticated' && matchList.length === 0 && !introductionEnded) {
      fetchMatches()
    }
  }, [authStatus, matchList.length, fetchMatches, introductionEnded])

  useEffect(() => {
    if (!match) return
    setBreadcrumbs([
      {
        label: 'Matches',
        path: '/matches',
        ...(location.state && { state: location.state }),
      },
      { label: match.profile.name },
    ])
  }, [match, setBreadcrumbs, location.state])

  useEffect(() => {
    if (!match) return
    clearProfile()
    fetchAttributes(match.profile.id)
  }, [match?.profile.id, clearProfile, fetchAttributes])

  if (authStatus !== 'authenticated') {
    return (
      <div className="matches-view">
        <div className="matches-view__loading">Loading...</div>
      </div>
    )
  }

  if (isLoading && !match && !introductionEnded) {
    return (
      <div className="matches-view">
        <div className="matches-view__loading">Loading match...</div>
      </div>
    )
  }

  if ((error || introductionEnded) && !match) {
    return (
      <div className="matches-view">
        <div className="matches-view__empty">
          <p className="matches-view__empty-text">
            {introductionEnded || error === 'This introduction has ended.'
              ? 'This introduction has ended.'
              : error || 'This match is no longer available.'}
          </p>
          <Button kind="ghost" text="Back to matches" onClick={() => navigate('/matches')} />
        </div>
      </div>
    )
  }

  if (!match) {
    return (
      <div className="matches-view">
        <div className="matches-view__empty">
          <p className="matches-view__empty-text">This match is no longer available.</p>
          <Button kind="ghost" text="Back to matches" onClick={() => navigate('/matches')} />
        </div>
      </div>
    )
  }

  const handleInterested = async () => {
    if (actionPending) return
    setActionPending(true)
    const result = await expressInterest(match.id)
    setActionPending(false)
    if (result.ended) {
      setIntroductionEnded(true)
      navigate('/matches')
    }
  }

  const handleUnmatch = async () => {
    if (actionPending) return
    setActionPending(true)
    const result = await unmatch(match.id)
    setActionPending(false)
    if (result.success && result.openRheya) openChat({ mode: 'unmatch' })
    if (result.success || result.ended) {
      setIntroductionEnded(true)
      navigate('/matches')
    }
  }

  const handleMessage = () => {
    navigate(`/matches/${match.id}/chat`)
  }

  const handleBlock = async () => {
    if (safetyLoading) return
    const result = await blockUser({ profileId: match.profile.id })
    if (result.success) {
      setSafetyMessage('User blocked. They will no longer appear in your matches.')
      await fetchMatches()
      navigate('/matches')
    }
  }

  const handleReport = async () => {
    if (safetyLoading) return
    const result = await reportUser({
      profileId: match.profile.id,
      reason: safetyForm.reason,
      ...(safetyForm.details.trim() && { details: safetyForm.details.trim() }),
    })
    if (result.success) {
      setSafetyMessage('Report submitted. Thank you for helping keep Revel Club safe.')
      setShowSafety(false)
      setSafetyForm((prev) => ({ ...prev, details: '' }))
    }
  }

  const safetySlot = (
    <>
      <Button
        className="person-profile__safety-toggle"
        kind="ghost"
        text="Report or block"
        onClick={() => setShowSafety((v) => !v)}
      />

      {showSafety && (
        <Form
          className="person-profile__safety-form"
          value={safetyForm}
          onChange={setSafetyForm}
          onSubmit={handleReport}
        >
          <SelectField name="reason" label="Reason" items={reportReasonItems} required />
          <TextField
            name="details"
            label="Details (optional)"
            placeholder="Anything else we should know"
            max={2000}
          />
          <div className="person-profile__safety-actions">
            <Button
              type="submit"
              kind="primary"
              text={safetyLoading ? 'Submitting…' : 'Submit report'}
              disabled={safetyLoading}
            />
            <Button
              kind="ghost"
              text={safetyLoading ? 'Blocking…' : 'Block user'}
              onClick={handleBlock}
              disabled={safetyLoading}
            />
          </div>
          {safetyError && <p className="person-profile__safety-error">{safetyError}</p>}
        </Form>
      )}

      {safetyMessage && <p className="person-profile__safety-message">{safetyMessage}</p>}
    </>
  )

  return (
    <div className="match-detail-view">
      <PersonProfile
        variant="match"
        name={match.profile.name}
        {...(match.profile.bio && { bio: match.profile.bio })}
        {...(match.profile.birthday && { birthday: match.profile.birthday })}
        {...(match.profile.location && { location: match.profile.location })}
        photoIds={match.profile.approvedPhotoIds}
        attributes={attributes}
        matchMeta={{
          score: match.score,
          theirScore: match.theirScore,
          yourScore: match.yourScore,
          status: match.status,
          yourDecision: match.yourDecision,
          awaitingThem: match.awaitingThem,
          ...(match.isSimulated !== undefined && { isSimulated: match.isSimulated }),
          ...(match.matchSummary && { matchSummary: match.matchSummary }),
          ...(match.highlights && match.highlights.length > 0 && { highlights: match.highlights }),
        }}
        onInterested={handleInterested}
        onUnmatch={handleUnmatch}
        onMessage={handleMessage}
        onAskRheya={() => {
          const chipText = (match.highlights ?? attributes?.chips ?? [])
            .map((chip) => chip.text)
            .join(', ')
          openChat({
            mode: 'general',
            initialContext: [
              `The member opened chat from ${match.profile.name}'s match profile (${match.profile.id}).`,
              `They clicked "Ask Rheya about this match".`,
              match.matchSummary
                ? `Your introduction note: ${match.matchSummary}`
                : 'There is no introduction note yet.',
              chipText ? `Visible chips: ${chipText}.` : '',
              'Explain why you introduced them, using only these match-visible facts.',
              "Do not reveal the other person's private preferences, notes, or unpublished details.",
            ]
              .filter(Boolean)
              .join(' '),
          })
        }}
        safetySlot={safetySlot}
      />
    </div>
  )
}
